/**
 * Phase 3 — Creator Claim Links panel (mounted on CreatorPortfolioDetail).
 * Endpoints:
 *   POST /api/creator/kickbacks/{id}/claim-links
 *   GET  /api/creator/kickbacks/{id}/claim-links
 *   PATCH /api/creator/claim-links/{claim_id}
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { QrCode, Copy, Pause, Play, Ban, Plus, X, ExternalLink, Edit3, Users } from 'lucide-react';
import theme from '../theme';
import SafeLanguageWarnings from './SafeLanguageWarnings';
import ClaimantsList from './ClaimantsList';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLAIM_TYPES = ['product', 'service', 'membership', 'employee_program', 'reward', 'utility_access', 'general'];

const STATUS_COLOR = {
  active: theme.colors.status.success, paused: theme.colors.status.warning,
  expired: theme.colors.text.muted, revoked: theme.colors.status.error,
  exhausted: theme.colors.text.muted,
};

const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, padding: 20, boxShadow: theme.colors.shadows.sm,
};
const inputStyle = {
  width: '100%', padding: '8px 10px', border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.sm, fontSize: 13, outline: 'none', boxSizing: 'border-box',
};

export default function ClaimLinksPanel({ portfolioId }) {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', claim_type: 'general',
    max_claims: '', max_redemptions: '', expires_at: '',
    requires_holder_login: true, allowed_holder_email: '',
  });
  const [safeWarnings, setSafeWarnings] = useState([]);
  const [patchWarnings, setPatchWarnings] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', description: '' });
  const [claimantsOpen, setClaimantsOpen] = useState(null);
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/creator/kickbacks/${portfolioId}/claim-links`, auth);
      setClaims(res.data?.claims || []);
    } catch (e) { toast.error('Failed to load claim links'); }
    finally { setLoading(false); }
  }, [portfolioId, auth]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setCreating(true);
    try {
      const payload = { ...form };
      ['max_claims', 'max_redemptions'].forEach(k => {
        payload[k] = payload[k] ? parseInt(payload[k], 10) : null;
      });
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      const res = await axios.post(`${API}/creator/kickbacks/${portfolioId}/claim-links`, payload, auth);
      toast.success('Claim link created');
      if (res.data?.plan_warning) toast.info(res.data.plan_warning);
      const w = res.data?.safe_language_warnings || [];
      setSafeWarnings(w);
      if (w.length === 0) setShowForm(false);
      setForm({ ...form, title: '', description: '' });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Create failed'); }
    finally { setCreating(false); }
  };

  const patchStatus = async (id, status) => {
    try {
      const res = await axios.patch(`${API}/creator/claim-links/${id}`, { status }, auth);
      toast.success(`Claim link ${status}`);
      // Phase 7F — surface any safe-language warnings returned from PATCH edits.
      setPatchWarnings(res.data?.safe_language_warnings || []);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const patchEditText = async (id, patch) => {
    try {
      const res = await axios.patch(`${API}/creator/claim-links/${id}`, patch, auth);
      toast.success('Claim link updated');
      setPatchWarnings(res.data?.safe_language_warnings || []);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success('Copied'); }
    catch { toast.error('Copy failed'); }
  };

  return (
    <div data-testid="claim-links-panel" style={{ ...cardStyle, marginTop: 16 }}>
      {/* Phase 7F — surface warnings from PATCH edits (title/description) */}
      <SafeLanguageWarnings warnings={patchWarnings} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 8 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <QrCode size={20} color={theme.colors.primary} />
            <div style={{ fontWeight: 800, fontSize: 17, color: theme.colors.text.primary }}>Claim Links & QR Codes</div>
          </div>
          <div style={{ fontSize: 12, color: theme.colors.text.muted, marginTop: 4 }}>
            Create claim links and QR codes holders can use to access or redeem this Kickback.
          </div>
        </div>
        <button data-testid="create-claim-link-btn" onClick={() => setShowForm(!showForm)}
          style={{ padding: '8px 14px', borderRadius: theme.radius.sm, border: 'none',
            background: theme.colors.primary, color: '#fff', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 700, fontSize: 13 }}>
          <Plus size={14} /> {showForm ? 'Close' : 'Create Claim Link'}
        </button>
      </div>

      {!loading && claims.length > 0 && (
        <div data-testid="claim-management-summary" style={{ display: 'flex', gap: 18, flexWrap: 'wrap', padding: '10px 12px', margin: '6px 0 14px', background: theme.colors.bg.secondary, borderRadius: theme.radius.sm, fontSize: 12 }}>
          {[
            ['Links', claims.length, 'summary-links'],
            ['Active', claims.filter(c => (c.effective_status || c.status) === 'active').length, 'summary-active'],
            ['Claims', claims.reduce((n, c) => n + (c.claims_used || 0), 0), 'summary-claims'],
            ['Entitlements', claims.reduce((n, c) => n + (c.entitlements_count || 0), 0), 'summary-entitlements'],
            ['NFTs issued', claims.reduce((n, c) => n + (c.issued_count || 0), 0), 'summary-issued'],
          ].map(([label, val, tid]) => (
            <div key={tid} data-testid={`claim-${tid}`}>
              <div style={{ color: theme.colors.text.muted }}>{label}</div>
              <div style={{ fontWeight: 800, fontSize: 16, color: theme.colors.text.primary }}>{val}</div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div data-testid="claim-link-form" style={{ padding: 14, background: theme.colors.bg.secondary, borderRadius: theme.radius.sm, marginBottom: 14 }}>
          <SafeLanguageWarnings warnings={safeWarnings} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginBottom: 10 }}>
            <div><label style={{ fontSize: 11, fontWeight: 600 }}>Title *</label>
              <input data-testid="claim-title" style={inputStyle} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
            <div><label style={{ fontSize: 11, fontWeight: 600 }}>Claim Type</label>
              <select data-testid="claim-type" style={inputStyle} value={form.claim_type} onChange={e => setForm({ ...form, claim_type: e.target.value })}>
                {CLAIM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select></div>
            <div><label style={{ fontSize: 11, fontWeight: 600 }}>Max Claims</label>
              <input data-testid="claim-max-claims" type="number" min="1" style={inputStyle} value={form.max_claims} onChange={e => setForm({ ...form, max_claims: e.target.value })} /></div>
            <div><label style={{ fontSize: 11, fontWeight: 600 }}>Max Redemptions</label>
              <input data-testid="claim-max-redemptions" type="number" min="1" style={inputStyle} value={form.max_redemptions} onChange={e => setForm({ ...form, max_redemptions: e.target.value })} /></div>
            <div><label style={{ fontSize: 11, fontWeight: 600 }}>Expires (ISO)</label>
              <input style={inputStyle} value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })} placeholder="2099-12-31T00:00:00Z" /></div>
            <div><label style={{ fontSize: 11, fontWeight: 600 }}>Allowed Holder Email</label>
              <input style={inputStyle} value={form.allowed_holder_email} onChange={e => setForm({ ...form, allowed_holder_email: e.target.value })} /></div>
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 11, fontWeight: 600 }}>Description</label>
            <textarea style={{ ...inputStyle, minHeight: 60, fontFamily: 'inherit' }} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
          </div>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 10 }}>
            <input data-testid="claim-requires-login" type="checkbox" checked={form.requires_holder_login} onChange={e => setForm({ ...form, requires_holder_login: e.target.checked })} />
            Requires holder login
          </label>
          <div>
            <button data-testid="claim-submit" disabled={creating} onClick={submit}
              style={{ padding: '10px 16px', borderRadius: theme.radius.sm, border: 'none',
                background: theme.colors.status.success, color: '#fff', cursor: 'pointer', fontWeight: 700 }}>
              {creating ? 'Creating…' : 'Create Claim Link'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div data-testid="claim-links-loading" style={{ color: theme.colors.text.muted, padding: 20 }}>Loading…</div>
      ) : claims.length === 0 ? (
        <div data-testid="claim-links-empty" style={{ color: theme.colors.text.muted, padding: 20, textAlign: 'center' }}>No claim links yet.</div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {claims.map(c => {
            const eff = c.effective_status || c.status;
            const color = STATUS_COLOR[eff] || theme.colors.text.muted;
            return (
              <div key={c.id} data-testid={`claim-row-${c.id}`} style={{ display: 'flex', gap: 14, padding: 12, background: theme.colors.bg.secondary, borderRadius: theme.radius.sm, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <img src={c.qr_data_url} alt="QR" style={{ width: 96, height: 96, borderRadius: 6, background: '#fff' }} />
                <div style={{ flex: 1, minWidth: 240 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <div style={{ fontWeight: 700, color: theme.colors.text.primary }}>{c.title}</div>
                    <span style={{ padding: '2px 8px', borderRadius: 999, background: `${color}1A`, color, fontSize: 10, fontWeight: 800, textTransform: 'uppercase' }} data-testid={`claim-status-${c.id}`}>{eff}</span>
                    <span style={{ fontSize: 11, color: theme.colors.text.muted }}>{c.claim_type}</span>
                  </div>
                  <div style={{ fontSize: 12, color: theme.colors.text.secondary, marginBottom: 6 }}>{c.description}</div>
                  <div style={{ fontSize: 11, color: theme.colors.text.muted, marginBottom: 8 }}>
                    Claims: {c.claims_used}/{c.max_claims ?? '∞'} · Redemptions: {c.redemptions_used}/{c.max_redemptions ?? '∞'}
                    {' · '}Entitlements: {c.entitlements_count ?? 0} · NFTs issued: {c.issued_count ?? 0}
                    {c.expires_at && ` · Expires ${new Date(c.expires_at).toLocaleDateString()}`}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <input readOnly value={c.claim_url} style={{ ...inputStyle, flex: 1, minWidth: 220, fontSize: 12 }} />
                    <button data-testid={`claim-copy-${c.id}`} onClick={() => copy(c.claim_url)}
                      style={{ padding: '6px 10px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}`, background: theme.colors.bg.card, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                      <Copy size={12} /> Copy
                    </button>
                    <a href={c.claim_url} target="_blank" rel="noreferrer" style={{ padding: '6px 10px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}`, background: theme.colors.bg.card, textDecoration: 'none', color: theme.colors.text.primary, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                      <ExternalLink size={12} /> Open
                    </a>
                    <button data-testid={`claim-claimants-${c.id}`} onClick={() => setClaimantsOpen(claimantsOpen === c.id ? null : c.id)}
                      style={{ padding: '6px 10px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}`, background: theme.colors.bg.card, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                      <Users size={12} /> {claimantsOpen === c.id ? 'Hide claimants' : 'Claimants'}
                    </button>
                    {c.status === 'active' && (
                      <button data-testid={`claim-pause-${c.id}`} onClick={() => patchStatus(c.id, 'paused')}
                        style={{ padding: '6px 10px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}`, background: theme.colors.bg.card, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                        <Pause size={12} /> Pause
                      </button>
                    )}
                    {c.status === 'paused' && (
                      <button data-testid={`claim-resume-${c.id}`} onClick={() => patchStatus(c.id, 'active')}
                        style={{ padding: '6px 10px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}`, background: theme.colors.bg.card, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                        <Play size={12} /> Resume
                      </button>
                    )}
                    {c.status !== 'revoked' && (
                      <button data-testid={`claim-edit-${c.id}`}
                        onClick={() => {
                          if (editingId === c.id) { setEditingId(null); return; }
                          setEditingId(c.id);
                          setEditForm({ title: c.title || '', description: c.description || '' });
                          setPatchWarnings([]);
                        }}
                        style={{ padding: '6px 10px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}`, background: theme.colors.bg.card, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                        <Edit3 size={12} /> {editingId === c.id ? 'Close' : 'Edit'}
                      </button>
                    )}
                    {c.status !== 'revoked' && (
                      <button data-testid={`claim-revoke-${c.id}`} onClick={() => patchStatus(c.id, 'revoked')}
                        style={{ padding: '6px 10px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}`, background: theme.colors.bg.card, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: theme.colors.status.error }}>
                        <Ban size={12} /> Revoke
                      </button>
                    )}
                  </div>
                  {claimantsOpen === c.id && <ClaimantsList claimId={c.id} auth={auth} />}
                  {editingId === c.id && (
                    <div data-testid={`claim-edit-form-${c.id}`} style={{ marginTop: 10, padding: 12, background: theme.colors.bg.card, borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}` }}>
                      <div style={{ marginBottom: 8 }}>
                        <label style={{ fontSize: 11, fontWeight: 600 }}>Title</label>
                        <input data-testid={`claim-edit-title-${c.id}`} style={inputStyle} value={editForm.title} onChange={e => setEditForm({ ...editForm, title: e.target.value })} />
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <label style={{ fontSize: 11, fontWeight: 600 }}>Description</label>
                        <textarea data-testid={`claim-edit-desc-${c.id}`} style={{ ...inputStyle, minHeight: 60, fontFamily: 'inherit' }} value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} />
                      </div>
                      <button data-testid={`claim-edit-save-${c.id}`}
                        onClick={async () => {
                          await patchEditText(c.id, { title: editForm.title, description: editForm.description });
                          setEditingId(null);
                        }}
                        style={{ padding: '8px 14px', borderRadius: theme.radius.sm, border: 'none', background: theme.colors.primary, color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>
                        Save Changes
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
